import Attachment from '@@types/Attachment';
import { CHANNEL_ID, LOG_ID, MEMBER_ID, UUID } from '@@types/common';
import Log, {
  AllLog,
  AllSearchLog,
  CommandLog,
  CommonDummyLog,
  DummyLog,
  FileDummyLog,
  FileLog,
  ForwardDummyLog,
  ForwardLog,
  InviteMemberLog,
  MailLog,
  ReplyDummyLog,
  ReplyLog,
  StickerDummyLog,
  StickerLog,
  SystemLog,
  TextDummyLog,
  TextLog,
  ThreadLogInMain,
  VoipLog,
  WebhookLog,
} from '@@types/Log';
import { LogChunk } from '@@types/Log/Chunk';

import { Chunk } from '~/components/ChunkRenderer/ChunkRenderer';
import { CHUNK_TYPE, LOG_FLAGS, LOG_TYPE, URLS } from '~/constants';
import i18n from '~/i18n';
import GA from '~/services/GA/GA';
import logging from '~/stores/providers/logging';
import { canRenderImage, getMimeType } from '~/utils/fileLogUtil';
import { memoizeCappedWith } from '~/utils/memoizeUtil';
import { lift, mEither } from '~/utils/monadUtil';
import { cloneDeep, isEmpty, isNumber, size } from 'lodash';
import {
  allPass,
  always,
  chain,
  complement,
  cond,
  converge,
  curry,
  dissoc,
  dropLast,
  eqProps,
  equals,
  filter,
  flatten,
  identity,
  ifElse,
  includes,
  isNil,
  join,
  last,
  map,
  memoizeWith,
  pipe,
  prop,
  propEq,
  propOr,
  propSatisfies,
  reject,
  startsWith,
  T,
  toString,
  trim,
  uniq,
  when,
  without,
} from 'ramda';
import mergeRight from 'ramda/es/mergeRight';
import ramdaUseWith from 'ramda/es/useWith';
import { Either, Maybe } from 'ramda-fantasy';
import uuidv4 from 'uuid/v4';

import { addMinutes, formatDuration, isAfterFromNow, isInTimeUnit, startOf } from './dateUtil';
import { isNonNil } from './fnUtil/fnUtil';
import {
  chunkCodeBlock,
  chunkEmoji,
  chunkInlineCode,
  chunkMarkdownLink,
  chunkMention,
  chunkUrl,
  removeMarkdown,
  removeMarkdownToChunk,
  safeParseJsonText,
} from './textUtil/textUtil';

export const createNewToken = (): UUID => {
  return uuidv4();
};

export const getMentionedMemberIds = (text) => {
  const regex = /\[@.*?]\(dooray:\/\/\d+?\/members\/(\d+?)(?: "[a-z]+?")?\)/g;
  const memberIds: string[] = [];
  let matches = regex.exec(text);

  while (matches !== null) {
    // This is necessary to avoid infinite loops with zero-width matches
    if (matches.index === regex.lastIndex) {
      regex.lastIndex += 1;
    }
    memberIds.push(matches[1]);
    matches = regex.exec(text);
  }

  return memberIds;
};

const chunkWithChunked = curry((chunker, chunks: LogChunk[]) => {
  return pipe(
    map((chunk: LogChunk) => (chunk.type === CHUNK_TYPE.TEXT ? chunker(chunk.text) : chunk)),
    flatten
  )(chunks);
});

const joinTextChunks = (chunks: LogChunk[]) => {
  let lastChunk = null;
  return chunks.reduce((newChunks, chunk) => {
    if (chunk.type !== CHUNK_TYPE.TEXT || lastChunk?.type !== CHUNK_TYPE.TEXT) {
      lastChunk = cloneDeep(chunk);
      return [...newChunks, lastChunk];
    }
    lastChunk.text += chunk.text;
    return newChunks;
  }, []);
};

const chunkInlineCodeWithChunked = chunkWithChunked(chunkInlineCode);
const chunkMentionWithChunked = chunkWithChunked(chunkMention);
const chunkUrlWithChunked = chunkWithChunked(chunkUrl);
export const chunkEmojiWithChunked = chunkWithChunked(chunkEmoji);
export const chunkMarkdownLinkWithChunked = chunkWithChunked(chunkMarkdownLink);

export const chunkSearchedLog: (obj: { text?: string; message?: string }) => Chunk[] = memoizeCappedWith(
  pipe<any, any, any, any, any, any>(
    ({ text, message }) => text || message,
    chunkMention,
    chunkEmojiWithChunked,
    chunkUrlWithChunked,
    joinTextChunks
  ),
  ({ text, message }) => text || message
);

export const chunkMentionEmoji = memoizeCappedWith(
  pipe<any, any, any, any>(chunkMention, chunkEmojiWithChunked, joinTextChunks)
);

export const chunkMentionUrlText: (text: string) => Record<string, unknown>[] = pipe(chunkMention, chunkUrlWithChunked);

export const chunkText: (text: string) => Chunk[] = memoizeCappedWith(
  pipe(
    chunkCodeBlock,
    chunkInlineCodeWithChunked,
    chunkMentionWithChunked,
    chunkMarkdownLinkWithChunked,
    chunkUrlWithChunked,
    chunkEmojiWithChunked,
    joinTextChunks
  )
);

export const chunkEmojiOnly: (text: string) => any[] = memoizeCappedWith(
  pipe(removeMarkdown, chunkEmoji, joinTextChunks)
);

export const chunkEmojiAndLink: (text: string) => any[] = memoizeCappedWith(
  pipe(chunkMarkdownLink, removeMarkdownToChunk, chunkUrlWithChunked, chunkEmojiWithChunked, joinTextChunks)
);

const getFullMinutesGroup = (ts: number) => Math.floor(ts / 1000 / 60 / 5);

export const isSameMinutesGroup = (timestamp1, timestamp2) => {
  return getFullMinutesGroup(timestamp1) === getFullMinutesGroup(timestamp2);
};

export const isSameDayGroup = (timestamp1: number, timestamp2: number) => {
  return isInTimeUnit('day', timestamp1, timestamp2);
};

export const extractCommandName = (text) => {
  return startsWith('/', text) ? text.split(' ')[0] : '';
};

export const extractParamStr = (text) => {
  return text.indexOf(' ') === -1 ? '' : text.substr(text.indexOf(' ')).trim();
};

export const extractStatus = (text) => {
  return extractParamStr(text).toLocaleLowerCase().replace('online', 'active');
};

export const extractMentionTypeAndId = (text) => {
  const matches = text.match(/\[[#@].*?\]\(dooray:\/\/\d+\/\w+\/(\d+) "(\w+)"\)/);
  if (!matches) return [];
  return {
    id: matches[1],
    type: matches[2],
  };
};

const extractMentionAndRest = (text: string) => {
  const regex = /\[@.+?\]\(dooray:\/\/\d+\/members\/(\d+) "\w*?"\)/gi;
  const result = [];
  let match = regex.exec(text);
  let lastIndex: number;

  if (match !== null) {
    do {
      result.push(text.slice(match.index, regex.lastIndex));
      lastIndex = regex.lastIndex;
      match = regex.exec(text);
    } while (match);
  }

  result.push(text.slice(lastIndex));
  return result;
};

const splitParams = pipe(extractParamStr, extractMentionAndRest);

export const extractMentionMembersId = (text): MEMBER_ID[] => {
  const result = [];
  const INVITEE_MEMBER_ID_REGEX = /\[@.+?\]\(dooray:\/\/\d+\/members\/(\d+) "\w*?"\)/gi;
  const matches = text.match(INVITEE_MEMBER_ID_REGEX);
  if (!matches) return result;
  return pipe<any, any, any>(
    uniq,
    map((invitee: string) => {
      INVITEE_MEMBER_ID_REGEX.lastIndex = 0;
      return INVITEE_MEMBER_ID_REGEX.exec(invitee)[1];
    })
  )(matches);
};

export const hasMentionBadge = (text) => {
  return /\[@.+?\]\(dooray:\/\/\d+\/members\/(\d+) "\w*?"\)/gi.test(text);
};

const extractTitle = pipe(last, ifElse(hasMentionBadge, always(''), identity), trim);

export const extractPopupParams = (text) => {
  return pipe<any, any, any>(
    lift(splitParams),
    converge(
      (membersId, title) => ({ membersId, title }),
      [
        pipe<any, any, any, any>(map(dropLast(1)), map(join(' ')), chain(pipe(extractMentionMembersId))),
        chain(extractTitle),
      ]
    )
  )(text);
};

export const getTextByVoIp = ({ type, duration }) => {
  return cond([
    [equals('offer'), always(i18n.t('voIpRequestCall', '음성 통화를 요청합니다.'))],
    [equals('dismiss'), always(i18n.t('voIpCancelCall', '통화를 취소합니다.'))],
    [allPass([equals('close'), () => isNumber(duration)]), always(formatDuration(duration))],
    [equals('close'), always(i18n.t('voIpFinishCall', '통화가 종료되었습니다.'))],
    [equals('busy'), always(i18n.t('voIpBusy', '응답이 없습니다.'))],
    [equals('decline'), always(i18n.t('voIpBusy', '응답이 없습니다.'))],
    [equals('timeout'), always(i18n.t('voIpBusy', '응답이 없습니다.'))],
    [T, always('')],
  ])(type);
};

export const getVoIpIconByType = (type) => {
  return cond([
    [equals('offer'), always('voIpOffer')],
    [equals('dismiss'), always('voIpDismiss')],
    [equals('close'), always('voIpLeave')],
    [T, always('voIpMissed')],
  ])(type);
};

const isTranslationAttachment: (x: Attachment) => boolean = propSatisfies(
  allPass([isNonNil, equals('translate')]),
  'type'
);

export const filterTranslations = pipe<any, Attachment[], Attachment[], Attachment[]>(
  propOr([], 'attachments'),
  filter((value) => !!value),
  filter(isTranslationAttachment)
);

export const filterNormalAttachments = pipe<any, Attachment[], Attachment[], Attachment[]>(
  propOr([], 'attachments'),
  filter((value) => !!value),
  reject(isTranslationAttachment)
);

export const isLogChanged = (log, prevLog) => {
  return (
    !eqProps('text', log, prevLog) ||
    !eqProps('attachments', log, prevLog) ||
    !equals(size(log.attachments), size(prevLog.attachments))
  );
};

export const findInviteLogsByMasterLogId = curry((masterLogId: string, logs: AllLog[]): InviteMemberLog[] => {
  return filter((log) => {
    return isInviteMemberLog(log) && propEq('masterLogId', masterLogId)(log);
  }, logs) as InviteMemberLog[];
});

export const deleteInviteLogIdByMasterLogId = curry((masterLogId: string, logs: Log[]) => {
  return converge<any, any, any, any>(without, [findInviteLogsByMasterLogId(masterLogId), identity])(logs);
});

export const isForwardLog = (log: { type: LOG_TYPE }): log is ForwardLog | ForwardDummyLog =>
  log.type === LOG_TYPE.FORWARD;

export const isReplyLog = (log: { type: LOG_TYPE }): log is ReplyLog | ReplyDummyLog => log.type === LOG_TYPE.REPLY;

export const isCommandLog = (log: { type: LOG_TYPE }): log is CommandLog => log.type === LOG_TYPE.COMMAND;

export const isNonCommandLog = complement(isCommandLog);

export const isWebHookLog = (log: { type: LOG_TYPE }): log is WebhookLog => log.type === LOG_TYPE.WEBHOOK;

export const isNonWebHookLog = complement(isWebHookLog);

export const isFileLog = (log: { type: LOG_TYPE }): log is FileLog | FileDummyLog => log.type === LOG_TYPE.FILE;

export const isInviteMemberLog = (log: { type: LOG_TYPE }): log is InviteMemberLog =>
  log.type === LOG_TYPE.INVITE_MEMBER;

export const isNonInviteMemberLog = complement(isInviteMemberLog);

export const isStickerLog = (log: { type: LOG_TYPE }): log is StickerLog | StickerDummyLog =>
  log.type === LOG_TYPE.STICKER;

export const isVoIpLog = (log: { type: LOG_TYPE }): log is VoipLog => log.type === LOG_TYPE.VOIP;

export const isNonVoIpLog = complement(isVoIpLog);

export const isEphemeralLog = propSatisfies(allPass([isNonNil, equals('ephemeral')]), 'responseType');

export const isNonEphemeralLog = complement(isEphemeralLog);

export const isTextLog = (log: { type: LOG_TYPE }): log is TextLog | TextDummyLog => log.type === LOG_TYPE.TEXT;

export const isSystemLog = (log: { type: LOG_TYPE }): log is SystemLog => log.type === LOG_TYPE.SYSTEM;

export const isMailLog = (log: { type: LOG_TYPE }): log is MailLog => log.type === LOG_TYPE.MAIL;

export const isNonMailLog = complement(isMailLog);

export const isDummyLog = (log: { type: LOG_TYPE }): log is DummyLog => propOr(false, '_dummy')(log);

export const isNonDummyLog = complement(isDummyLog);

// system, webhook, command, mail 로그는 overlap 불가
export const canOverlapLog = (log: { type: LOG_TYPE }) =>
  includes(log.type, [
    LOG_TYPE.INVITE_MEMBER,
    LOG_TYPE.TEXT,
    LOG_TYPE.FILE,
    LOG_TYPE.STICKER,
    LOG_TYPE.VOIP,
    LOG_TYPE.FORWARD,
    LOG_TYPE.REPLY,
  ]);

export const canCreateThreadLog = (log: { type: LOG_TYPE }) =>
  includes(log.type, [LOG_TYPE.TEXT, LOG_TYPE.FILE, LOG_TYPE.FORWARD, LOG_TYPE.REPLY, LOG_TYPE.WEBHOOK]);

export const validateEmpty = cond([
  [isEmpty, always(Either.Left('empty'))],
  [T, Either.Right],
]);

export const createEphemeralLog = (payload: {
  channelId: CHANNEL_ID;
  text: string;
  customName: string;
  senderId: MEMBER_ID;
  seq: number;
}): CommandLog => {
  const sentAt = new Date().getTime();
  return {
    ...payload,
    id: sentAt.toString(),
    customIconUrl: URLS.DEFAULT_BOT_IMAGE,
    responseType: 'ephemeral',
    type: LOG_TYPE.WEBHOOK,
    sentAt,
    flags: LOG_FLAGS.NORMAL,
  };
};

export type DummyRequiredProps = {
  channelId: CHANNEL_ID;
  senderId: MEMBER_ID;
  seq: number;
};

const getDefaultDummyLogProps = ({ channelId, senderId, seq }: DummyRequiredProps): CommonDummyLog => {
  const token = createNewToken();
  return {
    id: `dummy:${token}`,
    sentAt: Date.now() + 10000, // 서버 시간이 클라이언트 시간과 안맞는 경우가 있어서 방어코드 추가
    _dummy: true,
    channelId,
    token,
    senderId,
    seq,
    flags: LOG_FLAGS.NORMAL,
  };
};

// dummy log 아님
export const createInviteMemberLog = curry(
  (props: DummyRequiredProps, masterLogId: LOG_ID, inviteMemberId: MEMBER_ID): InviteMemberLog => {
    return dissoc('_dummy', {
      ...getDefaultDummyLogProps(props),
      text: inviteMemberId,
      type: LOG_TYPE.INVITE_MEMBER,
      masterLogId,
    });
  }
);

export const createDummyLog = curry((props: DummyRequiredProps, text: string): TextDummyLog => {
  return {
    ...getDefaultDummyLogProps(props),
    text,
    type: LOG_TYPE.TEXT,
  };
});

export const createFileDummyLog = curry((props: DummyRequiredProps, file: File): FileDummyLog => {
  return {
    ...getDefaultDummyLogProps(props),
    text: file.name,
    type: LOG_TYPE.FILE,
    file: {
      channelId: props.channelId,
      fileName: file.name,
      mimeType: getMimeType(file) || 'application/octet-stream',
      fileSize: file.size,
      title: '',
    },
    _attach: file,
  };
});

export const createFileDummyLogs = curry((props: DummyRequiredProps, files: File[]) => {
  return map(createFileDummyLog(props), files);
});

export const createStickerDummyLog = curry((props: DummyRequiredProps, stickerObj) => {
  return {
    ...getDefaultDummyLogProps(props),
    text: JSON.stringify(stickerObj),
    type: LOG_TYPE.STICKER,
  };
});

export const createForwardDummyLog = curry(
  (props: DummyRequiredProps, channelTitle: string, forwardLog: Log): ForwardDummyLog => {
    return {
      ...getDefaultDummyLogProps(props),
      type: LOG_TYPE.FORWARD,
      text: JSON.stringify({ channelTitle, log: forwardLog }),
    };
  }
);

export const createReplyDummyLog = curry(
  (props: DummyRequiredProps, originalLogId: LOG_ID, text: string): ReplyDummyLog => {
    return {
      ...getDefaultDummyLogProps(props),
      type: LOG_TYPE.REPLY,
      originalLogId,
      text: JSON.stringify({
        type: LOG_TYPE.TEXT,
        text,
      }),
    };
  }
);

export const createStickerReplyDummyLog = curry(
  (props: DummyRequiredProps, originalLogId: LOG_ID, stickerObj: any): ReplyDummyLog => {
    return {
      ...getDefaultDummyLogProps(props),
      type: LOG_TYPE.REPLY,
      originalLogId,
      text: JSON.stringify({
        type: LOG_TYPE.STICKER,
        text: JSON.stringify(stickerObj),
      }),
    };
  }
);

export const getLogDay = memoizeWith<(log: Log) => string>(
  ramdaUseWith(toString, [prop('sentAt')]),
  pipe(prop('sentAt'), startOf('day'), toString)
);

export const isInEditableTime = curry((periodMinutes: number, log: Log) => {
  return isAfterFromNow(addMinutes(log.sentAt, periodMinutes));
});

export const getTextWithTypeLog = (log: AllLog): AllLog => {
  return when(
    isReplyLog,
    pipe<any, any, any>(ramdaUseWith(safeParseJsonText, [prop('text')]), mEither(always(log), identity))
  )(log);
};

export const isControllable: (log: AllLog | AllSearchLog) => boolean = allPass([
  isNonDummyLog,
  (log: { type: LOG_TYPE }) =>
    includes(log.type, [
      LOG_TYPE.TEXT,
      LOG_TYPE.FILE,
      LOG_TYPE.STICKER,
      LOG_TYPE.FORWARD,
      LOG_TYPE.REPLY,
      LOG_TYPE.WEBHOOK,
    ]),
]);

export const getRenderableFiles = pipe<any, any, any, any>(
  filter(isFileLog),
  map(prop('file')),
  filter(canRenderImage)
);

const hasThreadChannel = (log: ThreadLogInMain) => {
  return log?.thread?.totalCount > 0;
};

export const canOverlap = (prevLog?: Log, log?: Log) => {
  return (
    log &&
    prevLog &&
    canOverlapLog(log) &&
    canOverlapLog(prevLog) &&
    !hasThreadChannel(prevLog as ThreadLogInMain) &&
    eqProps('senderId', log, prevLog) &&
    isSameMinutesGroup(log.sentAt, prevLog.sentAt)
  );
};

export const canOverlapInMainThread = (prevLog?: Log, log?: Log) => {
  return (
    log &&
    prevLog &&
    canOverlapLog(log) &&
    canOverlapLog(prevLog) &&
    eqProps('senderId', log, prevLog) &&
    (isInTimeUnit('day', log.sentAt, Date.now())
      ? isSameMinutesGroup(log.sentAt, prevLog.sentAt)
      : isSameDayGroup(log.sentAt, prevLog.sentAt))
  );
};

const getFixedReplyLogContent = (replyLog: AllLog | AllSearchLog): Maybe<AllLog | AllSearchLog> => {
  const text = 'text' in replyLog ? replyLog.text : 'message' in replyLog ? replyLog.message : '';
  return text
    ? safeParseJsonText(text).map(mergeRight(replyLog)).either(Maybe.Nothing, Maybe.Just)
    : Maybe.Just(replyLog);
};

export const getForwardedLog = (log: AllLog | AllSearchLog): Maybe<AllLog | AllSearchLog> => {
  // forwardLog는 원본 메시지가 text 안에 json 형태로 저장 되기 때문에
  // 재귀적으로 forwardLog가 아닐때까지 풀어준다
  const text = 'text' in log ? log.text : 'message' in log ? log.message : '';
  return text ? safeParseJsonText(text).map(prop('log')).either(Maybe.Nothing, getPreviewLog) : Maybe.Just(log);
};

export const getPreviewLog = (log: AllLog | AllSearchLog): Maybe<AllLog | AllSearchLog> => {
  return cond<[AllLog | AllSearchLog], Maybe<AllLog | AllSearchLog>>([
    [isReplyLog, getFixedReplyLogContent],
    [isForwardLog, getForwardedLog],
    [T, Maybe.Just],
  ])(log);
};

export const getSenderId = (log: AllLog | AllSearchLog): MEMBER_ID => {
  if ('senderId' in log) {
    return log.senderId;
  } else {
    return log.memberId;
  }
};

export const verifyRequiredProp = (key: keyof Log, log: Log) => {
  if (isNil(log[key])) {
    logging.debug(`[id=${log.id}][key=${key}][value=${log[key]}] log invalid field`);
    GA.writeRequiredLogField(key, log.id);
  }
};
